import React, { useEffect, useState } from "react";
import ssoApi, { TotpEnrollment } from "../api/sso/ssoApi";
import colors from "../designSystem/colors";

const { UI } = colors;

export interface MfaChallenge {
  organizationId: string | null;
  memberId: string | null;
  intermediateToken: string | null;
  email?: string;
  // No TOTP registered yet: enroll first (QR + recovery codes), then verify.
  needsEnrollment: boolean;
}

interface MfaStepProps {
  challenge: MfaChallenge;
  onSuccess: (session: any) => void;
  onBack: () => void;
}

const buttonStyle: React.CSSProperties = {
  padding: "11px 14px",
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 600,
  minHeight: 42,
  width: "100%",
  border: "none",
  background: UI.TEXT.ACTIVE,
  color: "#fff",
};

// TOTP step shared by the password (LoginPage) and discovery (SSOCallback) flows.
// Owns enrollment and verification; the page supplies the challenge and decides
// what to do with the authenticated session.
const MfaStep: React.FC<MfaStepProps> = ({ challenge, onSuccess, onBack }) => {
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = {
    organization_id: challenge.organizationId,
    member_id: challenge.memberId,
    intermediate_session_token: challenge.intermediateToken,
  };

  useEffect(() => {
    if (!challenge.needsEnrollment) return;
    let active = true;
    setLoading(true);
    ssoApi
      .mfaEnroll(params)
      .then((res) => {
        if (!active) return;
        if (res.status === 200) setEnrollment(res.body);
        else setError(res.body.detail || "Could not start MFA setup. Please try again.");
      })
      .catch(() => active && setError("Network error during MFA setup. Please try again."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // Enroll once per challenge — each call would mint a new TOTP secret.
  }, [challenge]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 6 || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ssoApi.mfaVerify({ ...params, code, email: challenge.email });
      if (res.status === 200) {
        onSuccess(res.body);
        return;
      }
      setError(
        res.body.error === "invalid_code"
          ? "Invalid or expired code. Please try again."
          : res.body.detail || "Verification failed. Please try again."
      );
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const disabled = loading || code.length < 6;

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {enrollment?.qr_code && (
        <div style={{ textAlign: "center" }}>
          <img
            src={
              enrollment.qr_code.startsWith("data:")
                ? enrollment.qr_code
                : `data:image/png;base64,${enrollment.qr_code}`
            }
            alt="TOTP QR Code"
            style={{ width: 160, height: 160, margin: "0 auto" }}
          />
          <p style={{ fontSize: 12, color: UI.TEXT.GREY, marginTop: 6 }}>
            Secret: <code style={{ userSelect: "all" }}>{enrollment.secret}</code>
          </p>
          {/* Stytch returns these only once, at enrollment — without them a lost phone means a manual MFA reset. */}
          {enrollment.recovery_codes?.length > 0 && (
            <div style={{ fontSize: 12, color: UI.TEXT.GREY, marginTop: 12, textAlign: "left" }}>
              <p style={{ margin: "0 0 6px", fontWeight: 600 }}>
                Save these recovery codes somewhere safe. Each works once if you lose your
                authenticator.
              </p>
              <code style={{ display: "block", whiteSpace: "pre", userSelect: "all" }}>
                {enrollment.recovery_codes.join("\n")}
              </code>
            </div>
          )}
        </div>
      )}

      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        placeholder="123456"
        autoFocus
        style={{
          padding: "8px 12px",
          borderRadius: 10,
          border: `1px solid ${UI.BORDER.STANDARD}`,
          fontSize: 24,
          letterSpacing: 6,
          textAlign: "center",
          boxSizing: "border-box",
          width: "100%",
        }}
      />

      {error && <p style={{ color: UI.TEXT.DANGER, fontSize: 13, margin: 0 }}>{error}</p>}

      <button
        type="submit"
        disabled={disabled}
        style={{
          ...buttonStyle,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        {loading ? "Verifying…" : "Verify code"}
      </button>

      <button
        type="button"
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          color: UI.TEXT.LIGHT,
          cursor: "pointer",
          fontSize: 13,
          textDecoration: "underline",
        }}
      >
        ← Back to login
      </button>
    </form>
  );
};

export default MfaStep;
